import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { dateToUtc } from '../../../functions';
import { RootState } from '../../../redux/rootReducer';
import { getDashSeparatedDate } from '../../fuctions';
import { Project } from '../../interfaces';
import { getJobSummary, getProject } from '../api';
import { emptyJobDetails } from '../constants';
import { getTwentyFourHourTime } from '../functions';
import { JobSummary, JobSummaryQueryPayload } from '../interfaces';

export default function useProjectView() {
  const userData: any = useSelector(
    (state: RootState) => state?.mainAppData?.userData,
  );
  const initialState = {
    userEmail: userData.email || ' - ',
    creationDate: ' - ',
    creationTime: ' - ',
    search: '',
    jobSummaryList: [],
    loadingJobSummaries: false,
    page: 1,
    pageSize: 25,
    count: 0,
    pages: 0,
    selectedProject: { code: '', name: '' },
    editJobDetailsModalOpen: false,
  };

  const [userEmail, setUserEmail] = useState<string>(initialState.userEmail);
  const [creationDate, setCreationDate] = useState<string>(
    initialState.creationDate,
  );
  const [creationTime, setCreationTime] = useState<string>(
    initialState.creationTime,
  );
  const [search, setSearch] = useState<string>(initialState.search);
  const [jobSummaryList, setJobSummaryList] = useState<JobSummary[]>(
    initialState.jobSummaryList,
  );
  const [loadingJobSummaries, setLoadingJobSummaries] = useState<boolean>(
    initialState.loadingJobSummaries,
  );
  const [page, setPage] = useState<number>(initialState.page);
  const [pageSize, setPageSize] = useState<number>(initialState.pageSize);
  const [count, setCount] = useState<number>(initialState.count);
  const [pages, setPages] = useState<number>(initialState.pages);
  const [selectedProject, setSelectedProject] = useState<
    Project | { code: string; name?: string }
  >(initialState.selectedProject);
  const [editJobDetailsModalOpen, setEditJobDetailsModalOpen] =
    useState<boolean>(initialState.editJobDetailsModalOpen);
  const [editingSummary, setEditingSummary] =
    useState<JobSummary>(emptyJobDetails);
  const location = useLocation();
  const projectId: any = new URLSearchParams(location?.search).get(
    'project_id',
  );
  const breadcrumbPath = [
    {
      text: 'Project',
      to: '/app/project',
    },
    {
      text: selectedProject?.name || '',
    },
  ];

  const curJobSummariesPayload = {
    search,
    page,
    pageSize,
    projectCode: selectedProject?.code,
  };

  const fetchProject = async (id: number) => {
    const project = await getProject(id, setLoadingJobSummaries);
    setSelectedProject(project);
    const date = dateToUtc(new Date(project.startDate));
    setCreationDate(getDashSeparatedDate(date));
    setCreationTime(getTwentyFourHourTime(date));
    return project;
  };

  const fetchJobSummaries = async (payload: JobSummaryQueryPayload) => {
    const { jobSummary, count, current, pages } = await getJobSummary(
      payload,
      setLoadingJobSummaries,
    );

    setJobSummaryList(jobSummary);
    setCount(count);
    setPages(pages);
    setPage(current);
  };

  const onChangeSearch = (newSearch: string) => {
    setSearch(newSearch);
    fetchJobSummaries({
      ...curJobSummariesPayload,
      search: newSearch,
    });
  };

  const onChangePage = (newPage: number) => {
    setPage(newPage);
    fetchJobSummaries({
      ...curJobSummariesPayload,
      page: newPage,
    });
  };

  const onChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    fetchJobSummaries({
      ...curJobSummariesPayload,
      pageSize: newPageSize,
    });
  };

  const onClickEdit = (id: number) => {
    const summary = jobSummaryList.find((summary: JobSummary) => {
      return summary.id === id;
    });
    if (summary) {
      setEditingSummary(summary);
      setEditJobDetailsModalOpen(true);
    }
  };

  const onCloseModal = () => {
    setEditJobDetailsModalOpen(false);
  };

  const onModalSave = () => {
    setEditJobDetailsModalOpen(false);
    fetchJobSummaries({
      search,
      page,
      pageSize,
      projectCode: selectedProject.code,
    });
  };

  useEffect(() => {
    setUserEmail(initialState.userEmail);
    setCreationDate(initialState.creationDate);
    setCreationTime(initialState.creationTime);
    setSearch(initialState.search);
    setJobSummaryList(initialState.jobSummaryList);
    setLoadingJobSummaries(initialState.loadingJobSummaries);
    setPage(initialState.page);
    setPageSize(initialState.pageSize);
    setCount(initialState.count);
    setPages(initialState.pages);
    setSelectedProject(initialState.selectedProject);

    fetchProject(projectId).then((project: Project | { code: string }) => {
      fetchJobSummaries({
        search: initialState.search,
        page: initialState.page,
        pageSize: initialState.pageSize,
        projectCode: project.code,
      });
    });
  }, []);

  return {
    breadcrumbPath,
    userEmail,
    creationDate,
    creationTime,
    search,
    onChangeSearch,
    jobSummaryList,
    loadingJobSummaries,
    page,
    onChangePage,
    pageSize,
    onChangePageSize,
    count,
    pages,
    onClickEdit,
    editJobDetailsModalOpen,
    editingSummary,
    onCloseModal,
    onModalSave,
  };
}
